import {TableContainer, TableBody, TableCell, TableHead, Snackbar, TableRow, Alert, Table, Paper} from '@mui/material';
import React, { useState, useEffect } from 'react';
import {useTranslation} from "react-i18next";
import {useNavigate} from 'react-router';
import {observer} from 'mobx-react';
import {BR_CODE_NOT_IN_USE, ECO_NOT_IN_USE, TWITCH_NOT_IN_USE} from './search/FilterOther.js';
import {TT_SPEC, ts, dStr, splitDate, toUnixT} from './Util.js';
import {useGlobalStorage} from './mobx/GlobalStorage.js';
import {defaultFilter} from './search/SearchDialog.js';
import {getSpecName, getCityName} from './Trans.js';
import {getTabStr} from './stats/StatsCompare.js';
import {TAB_OBS} from './stats/StatsCommon.js';
import {LIST_MAP_MAX_ELEM} from './ListMap.js';
import BottomBar from './BottomBar.js';
import ObsAppBar from './AppBar.js';
import {COMM_OK} from './WsR2.js';
import ObsRow from './ObsRow.js';
import PtsDialog from './Pts.js';
import BlDialog from './Bl.js';

function obsSumDesc(obs) {
	let desc = '';

	if (obs.id > 0)
		return obs.sum_desc.trim();

	obs.subobs.forEach((so) => {
		if (so.sum_desc.length) {
			if (desc.length)
				desc += ', ';
			desc += so.sum_desc;
		}
	});

	return desc;
}

export function obsNum(obs) {
	let n = 0;

	if (obs.id > 0) {
		n = obs.num;
	} else {
		obs.subobs.forEach((so) => {
			n += so.num;
		});
	}

	if (!n)
		return n

	return n + ' (' + obsSumDesc(obs) + ')';
}

/* -------------------------------------------------------------------------- */

/* 
 * msg: 	message (JSON) that is about to be modified
 * species:	array of wanted species
 * speclist:	full species list to get subspecies if needed
 */
export function specToMsg(msg, species, speclist) {
	species.forEach((elem) => {
		if (elem.t_type === TT_SPEC) {
			/* Add subspecies too */
			const ss_list = speclist.filter((x) => x.p_id === elem.id);
			ss_list.forEach((ss) => {
				/* But only if they are not yet in the list */
				const in_list = msg.data.species.find((x) => x === ss.id);
				if (!in_list) {
					msg.data.species.push(ss.id)
				}
			});
		}

		msg.data.species.push(elem.id)
	});
}

export function filterToMsg(f, msg, user_id, speclist) {
	if (!f.active)
		return;

	if (f.filter.time.t_type) {
		let d = splitDate(f.filter.time.start_t);
		if (d)
			msg.data.start = toUnixT(d[2], d[1], d[0], 0, 0);

		d = splitDate(f.filter.time.end_t);
		if (d)
			msg.data.end = toUnixT(d[2], d[1], d[0], 0, 0);
	}

	if (f.filter.country)
		msg.data.country = f.filter.country.id;

	specToMsg(msg, f.filter.species, speclist);

	// console.log('filterToMsg, spec_array', msg.data.species);

	f.filter.trips.forEach((elem) => msg.data.trips.push(elem.id));
	f.filter.cities.forEach((elem) => msg.data.city.push(elem.id));
	f.filter.asso.forEach((elem) => msg.data.asso.push(elem.id));

	if (f.filter.own_obs)
		msg.data.user = user_id;

	if (f.filter.no_coord)
		msg.data.no_coord = 1;

	msg.data.br_code_mask = f.filter.br_code_mask;
	msg.data.br_code = f.filter.br_code;
	msg.data.eco = f.filter.eco;
	msg.data.twitched = f.filter.twitched;

	msg.data.coordinates = JSON.parse(JSON.stringify(f.filter.coordinates));
}

/* -------------------------------------------------------------------------- */

let tgl = true;
let prev_addr = '';
let prev_date = 0;
let prev_spec_id = 0;
let prev_num = 0;

function ObsTable(props) {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	if (!props.obslist || props.obslist.length <= 0)
		return null;

	function sortBySpecName(ev) {
		if (gStorage.main.sorting.primary === 1) {
			gStorage.main.sorting.setPrimarySorting(1, gStorage.main.sorting.pri_order ? 0 : 1);
		} else {
			gStorage.main.sorting.setPrimarySorting(1, 1);
		}

		gStorage.main.setForceLoad(true);
	}
	
	function sortByTime(ev) {
		if (gStorage.main.sorting.primary === 0) {
			gStorage.main.sorting.setPrimarySorting(0, gStorage.main.sorting.pri_order ? 0 : 1);
		} else {
			gStorage.main.sorting.setPrimarySorting(0, 0);
		}

		gStorage.main.setForceLoad(true);
	}

	function sortByLocation(ev) {
		if (gStorage.main.sorting.primary === 2) {
			gStorage.main.sorting.setPrimarySorting(2, gStorage.main.sorting.pri_order ? 0 : 1);
		} else {
			gStorage.main.sorting.setPrimarySorting(2, 1);
		}

		gStorage.main.setForceLoad(true);
	}

	function sortByCount(ev) {
		if (gStorage.main.sorting.primary === 3) {
			gStorage.main.sorting.setPrimarySorting(3, gStorage.main.sorting.pri_order ? 0 : 1);
		} else {
			gStorage.main.sorting.setPrimarySorting(3, 1);
		}

		gStorage.main.setForceLoad(true);
	}

	function sortSignSpecName() {
//  		console.log('sortSignSpecName', gStorage.main.sorting.primary, gStorage.main.sorting.pri_order, gStorage.main.sorting.pri_order === 1);
		if (gStorage.main.sorting.primary !== 1)
			return '';

		return gStorage.main.sorting.pri_order === 0 ? ' ↓' : ' ↑';
	}

	function sortSignTime() {
// 		console.log('sortSignTime', gStorage.main.sorting.primary, gStorage.main.sorting.pri_order, gStorage.main.sorting.pri_order === 0);

		if (gStorage.main.sorting.primary !== 0)
			return '';

		return gStorage.main.sorting.pri_order === 0 ? ' ↓' : ' ↑';
	}

	function sortSignLocation() {
		if (gStorage.main.sorting.primary !== 2)
			return '';

		return gStorage.main.sorting.pri_order === 0 ? ' ↓' : ' ↑';
	}

	function sortSignCount() {
		if (gStorage.main.sorting.primary !== 3)
			return '';

		return gStorage.main.sorting.pri_order === 0 ? ' ↓' : ' ↑';
	}

	return (
		<TableContainer component={Paper} elevation={0}>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell size='small' sx={{cursor: 'pointer', minWidth: 150}} onClick={sortBySpecName}>{t('main.species') + sortSignSpecName()}</TableCell>
						<TableCell size='small' sx={{cursor: 'pointer', minWidth: 80}} onClick={sortByTime}>{t('main.time') + sortSignTime()}</TableCell>
						<TableCell size='small' sx={{cursor: 'pointer', minWidth: 180}} onClick={sortByLocation}>{t('main.location') + sortSignLocation()}</TableCell>
						<TableCell size='small' sx={{cursor: 'pointer', minWidth: 120}} onClick={sortByCount} align='right'>{t('main.count') + sortSignCount()}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{ 
						props.obslist.map((obs, ind) => {
							if (ind <= 0 && gStorage.main.curr_pg <= 0) {
								tgl = true;
							} else {
								if (gStorage.main.sorting.primary === 0 || gStorage.main.sorting.primary === 2) {
									if (prev_addr !== obs.addr || dStr(prev_date) !== dStr(obs.start_t))
										tgl = !tgl;
								} else if (gStorage.main.sorting.primary === 1) {
									if (prev_spec_id !== obs.spec_id) {
										tgl = !tgl;
									}
								} else if (gStorage.main.sorting.primary === 3) {
									if (prev_num !== obs.num) {
										tgl = !tgl;
									}
								}
							}

							prev_addr = obs.addr;
							prev_date = obs.start_t;
							prev_spec_id = obs.spec_id;
							prev_num = obs.num;

							return <ObsRow {...props} key={obs.id} obs={obs} tgl={tgl} />
						}
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

let _ts1, _ts2;

const ObsMain = observer((props) => {
	const [ptsDlgOpen, setPtsDlgOpen] = useState(false);
	const [blDlgOpen, setBlDlgOpen] = useState(false);
	const [snackOpen, setSnackOpen] = useState(false);
	const [obslist, setObsList]  = useState([]);
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const navigate = useNavigate();

	/* ------------------------------------------------------------------ */

	function mainGetSpecName(id) {
		return getSpecName(gStorage.lists.speclist.list.find(option => option.id === id), gStorage.conn.user);
	}

	function mainGetCityName(id) {
		return getCityName(gStorage.lists.citylist.list.find(option => option.id === id), gStorage.conn.user);
	}

	function offlineSort(o1, o2) {
// 		console.log('offlineSort', gStorage.main.sorting.primary, gStorage.main.sorting.pri_order);

		if (gStorage.main.sorting.primary === 0) {
			let ret;

			if (gStorage.main.sorting.pri_order) {
				ret = o1.start_utc - o2.start_utc;
			} else {
				ret = o2.start_utc - o1.start_utc;
			}

			if (ret === 0) 
				ret = o1.id - o2.id;
			return ret;
		} else if (gStorage.main.sorting.primary === 1) {
			let n1 = mainGetSpecName(o1.spec_id);
			let n2 = mainGetSpecName(o2.spec_id);

			if (gStorage.main.sorting.pri_order) 
				return n1.localeCompare(n2, 'fi');

			return n2.localeCompare(n1, 'fi');
		} else if (gStorage.main.sorting.primary === 2) {
			let cn1 = mainGetCityName(o1.city_id);
			let cn2 = mainGetCityName(o2.city_id);

			let ret = cn1.localeCompare(cn2, 'fi');
			if (ret === 0)
				ret = o1.addr.localeCompare(o2.addr, 'fi');
	
			if (gStorage.main.sorting.pri_order)
				return ret;

			return -ret;
		} else if (gStorage.main.sorting.primary === 3) {
			let n1 = obsNum(o1);
			let n2 = obsNum(o2);

			if (gStorage.main.sorting.pri_order)
				return n1 - n2;

			return n2 - n1;
		}

		return 0;
	}

	function updateObsList() {
		let off_d;

		off_d = window.localStorage.getItem('offline_obs');
		if (off_d)
			off_d = JSON.parse(off_d);
		
		if (off_d && off_d.length) {
			// console.log('offline', off_d.length, off_d);

			gStorage.main.setNObs(off_d.length);
			setObsList(off_d.slice().sort(offlineSort).slice(gStorage.main.curr_pg * gStorage.main.pg_sz, ((gStorage.main.curr_pg + 1) * gStorage.main.pg_sz) - 1));
			return;
		} else if (gStorage.conn.online) {
			if (gStorage.conn.is_auth) {
				loadObs(gStorage.main.curr_pg)
			} else {
				console.log('main: online but not auth!');
			}
		}
	}

	useEffect(() => {
		updateObsList()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gStorage.main.curr_pg, gStorage.conn.is_auth]);

	useEffect(() => {
		if (gStorage.main.force_load) {
// 			console.log('Main: force_load');
			gStorage.main.setForceLoad(false);
			updateObsList()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gStorage.main.force_load]);

	useEffect(() => {
// 		console.log('main useEffect filter changed', gStorage.main.filter.changed);
		
		if (gStorage.main.filter.changed) {
			if (gStorage.main.curr_pg !== 0) {
				gStorage.main.setCurrPg(0);
			}

			gStorage.main.filter.setChanged(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gStorage.main.filter.changed]);

	useEffect(() => {
// 		console.log('Main useEffect', document.referrer);

		/* Disable forward-button */
		window.history.pushState(null, '#', '/');
	}, []);

	/* ------------------------------------------------------------------ */

	function getheader() {
		return t('main.title');
	}

	function loadTripStats() {
		let tripID = [];

		if (gStorage.conn.user.trip1 > 0)
			tripID.push(gStorage.conn.user.trip1);
		if (gStorage.conn.user.trip2 > 0)
			tripID.push(gStorage.conn.user.trip2);
		if (gStorage.conn.user.trip3 > 0)
			tripID.push(gStorage.conn.user.trip3);

		if (tripID.length <= 0) {
			gStorage.trips.setStats([]);
			return;
		}

		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'trip',
			op: 'stats',
			data: {
				id: tripID
			}
		};

		gStorage.conn.ws.send(msg, mainRxWs);
	}

	function mainRxWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('mainRxWs: error ', status);
			return -1;
		}

// 		console.log('mainRxWs', msg);

		if (msg.cmd === 'obs') {
			if (msg.op === 'list') {
				gStorage.main.setLoading(false);

				_ts2 = ts();

				if (msg.result === 'OK') {
// 					console.log('mainRxWs:', msg);

					if (msg.data.obslist) {
						gStorage.main.setNObs(msg.data.total);
						setObsList(msg.data.obslist);
					} else {
 						gStorage.main.setNObs(0);
						setObsList([]);
					}

					loadTripStats();
				} else {
					console.error('mainRxWs: obslist query failed! Result: ', msg.result);
				}

				let _ts_e = ts();

				console.log('mainRxWs: update done in:', _ts2 - _ts1, '+', _ts_e - _ts2, '=', _ts_e - _ts1, 'ms.');
			} else if (msg.op === 'rm_user') {
				if (msg.result === 'OK') {
					gStorage.main.setForceLoad(true);
				} else {
					console.error('mainRxWs: rm_user failed!', msg.result);
				}
			} else if (msg.op === 'del') {
				if (msg.result === 'OK') {
					gStorage.main.setForceLoad(true);
				} else {
					console.error('addRxWs: delete failed ' + msg.data.desc + '!');
					alert(t('add.delete_failed') + ': ' + msg.data.desc);
				}
			} else {
				console.error('mainRxWs: unknown operation', msg.cmd, msg.op, msg.result);
			}
		} else if (msg.cmd === 'trip') {
			if (msg.op === 'stats') {
				if (msg.result === 'OK') {
// 					console.log('mainRxWs: trip stats:', msg.data);

					gStorage.trips.setStats(msg.data.stats);
					setSnackOpen(gStorage.conn.user.trip_snackbar);
				} else {
					console.error('mainRxWs: trip stats query failed! Result: ', msg.result);
				}
			} else {
				console.error('mainRxWs: unknown operation', msg.cmd, msg.op, msg.result);
			}
		} else {
			console.error('mainRxWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}

	function mainRxMapWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('mainRxMapWs: error ', status);
			return -1;
		}

		if (msg.cmd === 'obs') {
			if (msg.op === 'list') {
				if (msg.result === 'OK') {
// 					console.log('statsRxWs: received', msg.data.obslist.length, 'observations.');
					gStorage.map.setHeader(t(getTabStr(TAB_OBS)));
					gStorage.map.setList(msg.data.obslist);
					navigate('/map');
				} else {
					console.error('mainRxMapWs: obslist query failed! Result: ', msg.result);
				}
			} else {
				console.error('mainRxMapWs: unknown operation', msg.cmd, msg.op, msg.result);
			}
		} else {
			console.error('mainRxMapWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}

	function getSortDesc(sort_by) {
		switch (sort_by) {
			case 0:
				return "time";
			case 1:
				return "species";
			case 2:
				return "location";
			case 3:
				return "count";
			default: 
				console.error('getSortDesc: invalid sort type', sort_by);
		}

		return "time";
	}

	function getSortOrderDesc(order) {
		switch (order) {
			case 0:
				return "desc";
			case 1:
				return "asc";
			default: 
				console.error('getSortOrderDesc: invalid sort order', order);
		}

		return "asc";
	}

	function loadObs(pg, forMap = false) {
		let city_arr = [], spec_arr = [], trip_arr = [], asso_arr = [];
		let dt = new Date();
		let tz = -dt.getTimezoneOffset() * 60;

		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'obs',
			op: 'list',
			data: {
				count: forMap ? LIST_MAP_MAX_ELEM : gStorage.main.pg_sz,
				pos: forMap ? 0 : pg * gStorage.main.pg_sz,
				country: 0,
				city: city_arr,
				species: spec_arr,
				trips: trip_arr,
				asso: asso_arr,
				br_code_mask: BR_CODE_NOT_IN_USE,
				br_code: 0,
				eco: ECO_NOT_IN_USE,
				twitched: TWITCH_NOT_IN_USE,
				tz: tz,
				sort_by: forMap ? 'time' : getSortDesc(gStorage.main.sorting.primary),
				sort_order: forMap ? 'desc' : getSortOrderDesc(gStorage.main.sorting.pri_order),
				coordinates: []
			},
		}

		filterToMsg(gStorage.main.filter, msg, gStorage.conn.user.id, gStorage.lists.speclist.list);
		// console.log('loadObs', msg);

		_ts1 = ts();

		gStorage.conn.ws.send(msg, forMap ? mainRxMapWs : mainRxWs);
		gStorage.main.setLoading(true);
	}

	function toAddObs() {
		gStorage.add.setFromVoice(false);
		navigate("/add");
	}
	
	function handleSnackClose(event, reason) {
		  if (reason === 'clickaway') {
			return;
		}

		setSnackOpen(false);
	}

	function tripInfo() {
		let ret = [];

		gStorage.trips.stats.forEach((trip, ind) => {
// 			console.log('tripInfo', ind, 'trip id:', trip.trip, 'species:', trip.taxons.length);

			if (trip.trip > 0 && trip.taxons.length > 0) {
				let id = trip.trip;
				let substr = '';

				const _trip = gStorage.lists.triplist.list.find((trip) => trip.id === id);
				
				if (_trip) {
					let num = 0, specs = 0;

					trip.taxons.forEach((t, ind) => {
						num += t.num;

						const _spec = gStorage.lists.speclist.list.find((spec) => spec.id === t.id);
						if (_spec && _spec.stats)
							specs++;
					});

					substr = _trip.name + ': ' + 
						trip.n_obs + ' ' + t('trips.observations') + ', ' + 
						specs + ' ' + t('trips.species') + ', ' + 
						num + ' ' + t('trips.num');
				}

				if (ind >= gStorage.trips.stats.length - 1) {
					ret.push(<Alert key={ind} onClose={handleSnackClose} severity="info">{substr}</Alert>);
				} else {
					ret.push(<Alert key={ind} severity="info">{substr}</Alert>);
				}
			}
		});

		return ret.reverse();
	}

	function blDlgClose(changed) {
		setBlDlgOpen(false);
		gStorage.conn.setBlNotification(null);

		if (gStorage.conn.pts_notification) {
			setPtsDlgOpen(true);
		}
	}

	function ptsDlgClose() {
		setPtsDlgOpen(false);
		gStorage.conn.setPtsNotification(null);
	}

	function toFilter() {
		navigate('/filter');
	}

	function toMap() {
		loadObs(0, true);
	}

	function filterIsDefault() {
		return gStorage.main.filter.active && JSON.stringify(gStorage.main.filter.filter) === JSON.stringify(defaultFilter);
	}

	function mainRecAction(cmd) {
		console.log('mainRecAction', cmd);

		if (cmd === 'stats') {
			navigate('/statistics');
		} else if (cmd === 'navigate') {
			navigate('/navi');
		} else if (cmd === 'taxons') {
			navigate('/taxons');
		} else if (cmd === 'add') {
			gStorage.add.setFromVoice(true);
			navigate('/add');
		} else {
			console.log('Unknown command', cmd);
		}
	}

	if (gStorage.conn.bl_notification) {
		if (blDlgOpen === false) {
			setBlDlgOpen(true);
		}
	} else if (gStorage.conn.pts_notification) {
		if (ptsDlgOpen === false) {
			setPtsDlgOpen(true);
		}
	}

	return (
		<div>
			<ObsAppBar getheader={getheader} add_action={toAddObs} enter_filter_fn={toFilter} filter_is_default={filterIsDefault} enter_map={toMap} />
			<ObsTable obslist={obslist} rxWs={mainRxWs} />
			<BottomBar 
				rec={{action: mainRecAction,continuous: false}}
			/>

			<Snackbar open={snackOpen} autoHideDuration={null} onClose={handleSnackClose}>
				<div>
					{tripInfo()}
				</div>
			</Snackbar>

			<BlDialog isOpen={blDlgOpen} handleClose={blDlgClose}/>
			<PtsDialog isOpen={ptsDlgOpen} handleClose={ptsDlgClose}/>
		</div>
	);
});

export default ObsMain;
